import React, { useState, useEffect, useRef } from "react";
import {
  Grid2,
  Typography,
  Button,
  Box,
  Paper,
  IconButton,
  Chip
} from "@mui/material";
import { usePermission } from "../authentication/permission-providor/UserPermission";
import { useDispatch, useSelector } from "react-redux";
import AddMedia from "./AddMedia";
import { getAssets, removeAsset } from "../../actions/mediaAction";
import { DataGrid } from "@mui/x-data-grid";
import AddButton from "../../themes/overrides/AddButton";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchField from "../../themes/overrides/SearchField";
import EditIcon from "../../components/icons/EditIcon";
import RemoveIcon from "../../components/icons/RemoveIcon";
import MediaIcon from "../../assets/images/brush.svg";
import ListIcon from "../../components/icons/ListIcon";
import GridIcon from "../../components/icons/GridIcon";
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import LoadingOverlay from "../../components/LoadingOverlay";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { useParams } from 'react-router-dom';
import { SkeletonRowsOverlay } from "../../components/SkeletonRowsOverlay";
import { useDebounce } from "../../components/useDebounce";

const MediaList = () => {
  const dispatch = useDispatch();
  // Assets carry no environmentId — the media library is workspace-wide and
    // shared by every environment — so it follows the workspace role, not the
    // role held in whichever environment the switcher happens to be on.
    const { hasWorkspacePermission: hasPermission } = usePermission();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 15,
  });


  const assets = useSelector((state) => state.assets.assets);
  const [loading, setLoading] = useState(() => {
    return !assets || assets.length === 0;
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteAsset, setSelectedDeleteAsset] = useState(null);

  const { workspaceId } = useParams();
  const paginationData = useSelector((state) => state.assets.pagination);
  const uploadSuccess = useSelector((state) => state.assets.success)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [viewMode, setViewMode] = useState("list");
  const [gridColor, setGridColor] = useState("#6B6B6B");
  const [listColor, setListColor] = useState("#6B6B6B");
  const [rowsLoading, setRowsLoading] = useState(false);
  const debouncedSearch = useDebounce(searchTerm, 500);

  const firstLoadRef = useRef(true);


  const openAddMediaModal = () => {
    setModalOpen(true);
    setEditMode(false);
    setSelectedAsset(null);
  };

  useEffect(() => {
    if (firstLoadRef.current) {
      setLoading(true); // first load
      firstLoadRef.current = false;
    } else {
      setRowsLoading(true); // subsequent loads
    }

    dispatch(getAssets(workspaceId, paginationModel.page + 1, debouncedSearch))
      .finally(() => {
        setLoading(false);
        setRowsLoading(false);
      });
  }, [workspaceId, paginationModel.page, paginationModel.pageSize, debouncedSearch, dispatch]);



  useEffect(() => {
    setPaginationModel({ page: 0, pageSize: 15 });
  }, [searchTerm]);

  const handleEdit = (id) => {
    const assetToEdit = assets.find((asset) => asset._id === id);
    if (assetToEdit) {
      setSelectedAsset(assetToEdit);
      setEditMode(true);
      setModalOpen(true);
    }
  };

  const handleDelete = (assetId) => {
    setSelectedDeleteAsset(assetId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    const success = await dispatch(removeAsset(selectedDeleteAsset));
    if (success) {
      dispatch(getAssets(workspaceId, paginationModel.page + 1, debouncedSearch));
    }
    setDeleteModalOpen(false);
    setDeleteLoading(false);
    setSelectedDeleteAsset(null);
  };

  const handleSubmitSuccess = () => {
    setModalOpen(false);
    dispatch(getAssets(workspaceId, paginationModel.page + 1, debouncedSearch));
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const handleToggleView = (view) => {
    if (view === "List") {
      setViewMode("list");
      setListColor("#006D70");
      setGridColor("#6B6B6B");
    } else {
      setViewMode("grid");
      setGridColor("#006D70");
      setListColor("#6B6B6B");
    }
  };

  const columns = [
    { field: "id", headerName: "id", width: 50,headerAlign: 'center', align: 'center' },
    {
      field: "url",
      headerName: "Image",
      headerAlign: 'center',
      align: 'center',
      width: 120, // Increased width
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={params.value}
            alt={`Asset ${params.row.id}`}
            style={{
              maxWidth: '100%',
              maxHeight: '100%', // Adjust to fit cell
              height: '100px', // Fixed height for consistency
              width: '100px',
              objectFit: 'contain', // Changed to contain to show full image
              borderRadius: '4px',
              border: '1px solid #eee',
              cursor: 'pointer',
            }}
            onClick={() => window.open(params.value, '_blank')} // Simple lightbox/preview alternative
          />
        </Box>
      ),
    },
    { field: "title", headerName: "File Name", flex: 1 , headerAlign: 'center', align: 'center'},
    { field: "type", headerName: "Type", flex: 0.5, headerAlign: 'center',align: 'center'},
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
          <Typography variant="body2">
            {params.value ? new Date(params.value).toLocaleDateString() + ' ' + new Date(params.value).toLocaleTimeString() : 'N/A'}
          </Typography>
        </Box>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            onClick={() => handleEdit(params.row._id)}
            aria-label="edit"
            disabled={!hasPermission('editor')}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => handleDelete(params.row._id)}
            aria-label="delete"
            disabled={!hasPermission('admin')}
          >
            <RemoveIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const rows = assets.map((asset, index) => ({
    id: index + 1 + paginationModel.page * paginationModel.pageSize,
    _id: asset._id,
    ...asset,
  }));


  return (
    loading ? <LoadingOverlay /> :
      <>
        <Grid2 container spacing={2} justifyContent="space-between" alignItems="center">
          <Grid2 container size={12} display="flex" justifyContent="space-between">
            <Grid2 size={{ xs: 12, md: 8 }} display='flex' alignItems="flex-start" sx={{ marginLeft: { xs: 4, md: 0 } }}>
              <Box component="img" sx={{ width: { xs: 32, md: 40 }, marginRight: { xs: 2, md: 4 } }} src={MediaIcon} />
              <Typography variant={"h1"}>All Media</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }} offset={{ xs: 4, md: 0 }} display="flex" justifyContent="flex-end">
              <AddButton
                label="Add Media"
                sx={{ mr: 1 }}
                onClick={openAddMediaModal}
                disabled={!hasPermission('editor')}
              />
            </Grid2>
          </Grid2>

          <Grid2 container size={12} spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <Grid2 size={12} sx={{ width: "100%", minWidth: { md: '100%' } }} >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexGrow: 1
                }}>

                <Box sx={{ width: { md: 300 } }}>
                  <SearchField
                    placeholder="Search Media"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearch}
                    sx={{ flex: 1 }}
                  />
                </Box>
                {/* <Box sx={{ border: '1px solid #6B6B6B', borderRadius: 2, background: 'white' }}>
                  <IconButton onClick={() => handleToggleView("List")}>
                    <ListIcon toggleColor={listColor} />
                  </IconButton>
                  <IconButton onClick={() => handleToggleView("Grid")}>
                    <GridIcon toggleColor={gridColor} />
                  </IconButton>
                </Box> */}
              </Box>
            </Grid2>
            <Grid2 size={12} sx={{ overflowX: 'auto' }}>
              <Paper elevation={2} sx={{ padding: 2, marginBottom: 2, marginTop: { md: 2 }, overflowX: 'auto' }}>
                {assets.length > 0 ? (
                  viewMode === "list" ? (
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                      {rowsLoading && rows.length > 0 ? (
                        <SkeletonRowsOverlay columnsCount={columns.length} rowsCount={rows.length} />
                      ) : (
                        <DataGrid
                          rows={rows}
                          columns={columns}
                          paginationModel={paginationModel}
                          onPaginationModelChange={setPaginationModel}
                          paginationMode="server"
                          rowCount={paginationData?.total ?? 0}
                          pageSizeOptions={[15]}
                          disableRowSelectionOnClick
                          rowHeight={120} // Increased row height for larger images
                          loading={rowsLoading && rows.length === 0}
                          slots={{
                            loadingOverlay: () => (
                              <SkeletonRowsOverlay columnsCount={columns.length} rowsCount={15} />
                            ),
                          }}
                        />
                      )}

                    </Box>
                  ) : (
                    <Grid2 container spacing={2} sx={{ flexWrap: 'wrap' }}>
                      {assets.map((data, i) => (
                        <Grid2 key={data._id} size={{ xs: 12, md: 3 }} paddingRight={1}>
                          {/* Content Card */}
                          <Box sx={{ border: "1px solid #ddd", borderRadius: 2 }}>
                            <Box
                              component="img"
                              sx={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'cover',
                              }}
                              src={data.url}
                              alt={data.title}
                            />
                            <Box p={2}>
                              <Typography variant="body1">{data.title}</Typography>
                              <Typography variant="body2">{data.type}</Typography>
                            </Box>
                            <Box paddingLeft={2} display="flex">
                              <IconButton
                                color="primary"
                                onClick={() => handleEdit(data._id)}
                                aria-label="edit"
                                disabled={!hasPermission('editor')}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="primary"
                                onClick={() => handleDelete(data._id)}
                                aria-label="delete"
                                disabled={!hasPermission('admin')}
                              >
                                <RemoveIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </Grid2>
                      ))}
                    </Grid2>
                  )
                ) : (
                  <Typography align="center">No Contents</Typography>
                )}
              </Paper>
            </Grid2>
          </Grid2>
          <AddMedia
            isOpen={modalOpen}
            onClose={() => {
              setModalOpen(false);
            }}
            onSubmitSuccess={handleSubmitSuccess}
            editMode={editMode}
            asset={selectedAsset}
          />
        </Grid2>

        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          text="Are you sure you want to delete this asset?"
          onConfirm={handleConfirmDelete}
          deleteButtonLabel="Delete"
          loading={deleteLoading}
        />
      </>
  );
};

export default MediaList;
